import React, { useState, useEffect, useRef } from 'react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { Mail, MessageSquare, Send, CheckCircle, XCircle, Eye, EyeOff, RefreshCw, FileText, Plus, Edit3, Trash2, Save, Settings } from 'lucide-react';
import NotificationControlPanel from './NotificationControlPanel';

const NotificationSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [activeTab, setActiveTab] = useState('control-panel');
  const [activeChannel, setActiveChannel] = useState('email');
  const [templates, setTemplates] = useState([]);
  const [defaultTemplates, setDefaultTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showDefaultTemplatesModal, setShowDefaultTemplatesModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);

  const defaultEmail = {
    channel: 'EMAIL',
    enabled: false,
    config: { host: '', port: '587', secure: false, username: '', password: '', fromEmail: '' }
  };

  const defaultWhatsApp = {
    channel: 'WHATSAPP',
    enabled: false,
    config: { serviceEndpoint: 'http://localhost:3001/whatsapp', sessionId: 'default', apiKey: '' }
  };

  useEffect(() => { fetchSettings(); fetchTemplates(); fetchDefaultTemplates(); }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/notifications/settings');
      setSettings(res.data || []);
    } catch {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get('/api/notifications/templates');
      setTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    }
  };

  const fetchDefaultTemplates = async () => {
    try {
      const res = await axios.get('/api/notifications/default-templates');
      setDefaultTemplates(res.data || []);
    } catch (error) {
      console.log('Failed to load default templates:', error.message);
      setDefaultTemplates([]);
    }
  };

  const createDefaultTemplates = async () => {
    try {
      const res = await axios.get('/api/notifications/default-templates');
      setDefaultTemplates(res.data || []);
    } catch {
      console.log('Could not create default templates via API');
    }
  };

  const updateDefaultTemplate = async (template) => {
    try {
      await axios.put(`/api/notifications/default-templates/${template.id}`, template);
      toast.success('Default template updated');
      fetchDefaultTemplates();
    } catch {
      // Fallback to local update
      setDefaultTemplates(prev => 
        prev.map(t => t.id === template.id ? { ...template, updatedAt: new Date().toISOString() } : t)
      );
      toast.success('Default template updated locally');
    }
  };

  const resetDefaultTemplate = async (templateId) => {
    if (!window.confirm('Reset this template to its original default content? This cannot be undone.')) return;
    try {
      await axios.post(`/api/notifications/default-templates/${templateId}/reset`);
      toast.success('Template reset to default');
      fetchDefaultTemplates();
    } catch {
      // Fallback: recreate all default templates
      await createDefaultTemplates();
      toast.success('Templates reset to defaults');
    }
  };

  const toggleDefaultTemplate = async (template) => {
    try {
      await axios.put(`/api/notifications/default-templates/${template.id}`, {
        ...template,
        enabled: !template.enabled
      });
      toast.success(`Default template ${template.enabled ? 'disabled' : 'enabled'}`);
      fetchDefaultTemplates();
    } catch {
      // Fallback to local update
      setDefaultTemplates(prev => 
        prev.map(t => t.id === template.id ? { ...t, enabled: !t.enabled, updatedAt: new Date().toISOString() } : t)
      );
      toast.success(`Default template ${template.enabled ? 'disabled' : 'enabled'}`);
    }
  };

  const toggleTemplate = async (template) => {
    try {
      await axios.put(`/api/notifications/templates/${template.id}`, {
        ...template,
        enabled: !template.enabled
      });
      toast.success(`Template ${template.enabled ? 'disabled' : 'enabled'}`);
      fetchTemplates();
    } catch {
      toast.error('Failed to update template');
    }
  };

  const saveTemplate = async (template) => {
    try {
      if (template.id) {
        await axios.put(`/api/notifications/templates/${template.id}`, template);
        toast.success('Template updated');
      } else {
        await axios.post('/api/notifications/templates', template);
        toast.success('Template created');
      }
      fetchTemplates();
      setEditingTemplate(null);
      setShowTemplateModal(false);
    } catch {
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`/api/notifications/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get('/api/notifications/logs?limit=50&channel=EMAIL');
      setLogs(res.data.logs || []);
    } catch {
      toast.error('Failed to load logs');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    try {
      setTesting(p => ({ ...p, testEmail: true }));
      const res = await axios.post('/api/notifications/test', {
        channel: 'EMAIL',
        recipient: testEmail,
        subject: 'AGMS Test Email',
        message: `<h2>Test Email from AGMS</h2>
                  <p>This is a test email sent at ${new Date().toLocaleString()}</p>
                  <p>If you received this email, your SMTP configuration is working correctly.</p>
                  <p>Best regards,<br>AGMS System</p>`
      });
      
      if (res.data.success) {
        toast.success('Test email sent successfully!');
        fetchLogs(); // Refresh logs
      } else {
        toast.error(res.data.error || 'Failed to send test email');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setTesting(p => ({ ...p, testEmail: false }));
      setShowTestModal(false);
      setTestEmail('');
    }
  };

  const getChannelSettings = (channel) => {
    const found = settings.find(s => s.channel === channel);
    if (found) return { ...found, config: typeof found.config === 'string' ? JSON.parse(found.config) : found.config };
    return channel === 'EMAIL' ? defaultEmail : defaultWhatsApp;
  };

  const saveChannel = async (channel, enabled, config) => {
    try {
      await axios.put('/api/notifications/settings', { channel, enabled, config });
      toast.success(`${channel} settings saved`);
      fetchSettings();
    } catch {
      toast.error(`Failed to save ${channel} settings`);
    }
  };

  const testChannel = async (channel) => {
    setTesting(p => ({ ...p, [channel]: true }));
    try {
      const res = await axios.get(`/api/notifications/settings/test/${channel}`);
      if (res.data.success) toast.success(res.data.message || `${channel} connection OK`);
      else toast.error(res.data.error || `${channel} test failed`);
    } catch (err) {
      toast.error(err.response?.data?.error || `${channel} test failed`);
    } finally {
      setTesting(p => ({ ...p, [channel]: false }));
    }
  };

  const EmailPanel = () => {
    const s = getChannelSettings('EMAIL');
    const [form, setForm] = useState(s.config);
    const [enabled, setEnabled] = useState(s.enabled);

    useEffect(() => {
      const fresh = getChannelSettings('EMAIL');
      setForm(fresh.config);
      setEnabled(fresh.enabled);
    }, [settings]); // eslint-disable-line

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Email / SMTP Configuration</h3>
          </div>
          <button
            onClick={() => { setEnabled(!enabled); }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className={`grid grid-cols-2 gap-4 ${!enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
            <input type="text" value={form.host || ''} onChange={e => setForm({ ...form, host: e.target.value })}
              disabled={!enabled}
              placeholder="smtp.gmail.com" className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
            <input type="text" value={form.port || ''} onChange={e => setForm({ ...form, port: e.target.value })}
              disabled={!enabled}
              placeholder="587" className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username / Email</label>
            <input type="text" value={form.username || ''} onChange={e => setForm({ ...form, username: e.target.value })}
              disabled={!enabled}
              placeholder="your@email.com" className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password / App Password</label>
            <div className="relative">
              <input type={showPasswords.email ? 'text' : 'password'} value={form.password || ''}
                onChange={e => setForm({ ...form, password: e.target.value })}
                disabled={!enabled}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none pr-10 disabled:bg-gray-100 disabled:cursor-not-allowed" />
              <button type="button" onClick={() => setShowPasswords(p => ({ ...p, email: !p.email }))}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
                {showPasswords.email ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
            <input type="text" value={form.fromEmail || ''} onChange={e => setForm({ ...form, fromEmail: e.target.value })}
              disabled={!enabled}
              placeholder="noreply@yourdomain.com" className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed" />
          </div>
          <div className="flex items-end">
            <label className={`flex items-center gap-2 ${!enabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input type="checkbox" checked={form.secure || false} onChange={e => setForm({ ...form, secure: e.target.checked })}
                disabled={!enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed" />
              <span className="text-sm text-gray-700">Use SSL/TLS (port 465)</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={() => saveChannel('EMAIL', enabled, form)} disabled={!enabled}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <CheckCircle className="w-4 h-4" /> Save Email Settings
          </button>
          <button onClick={() => testChannel('EMAIL')} disabled={testing.EMAIL || !enabled}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            {testing.EMAIL ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Test Connection
          </button>
          <button onClick={() => setShowTestModal(true)} disabled={!enabled}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Mail className="w-4 h-4" /> Send Test Email
          </button>
          <button onClick={() => { fetchLogs(); setShowLogsModal(true); }}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2">
            <Eye className="w-4 h-4" /> View Logs
          </button>
        </div>
      </div>
    );
  };

  const WhatsAppPanel = () => {
    const s = getChannelSettings('WHATSAPP');
    const [form, setForm] = useState(s.config);
    const [enabled, setEnabled] = useState(s.enabled);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [qrCode, setQrCode] = useState('');
    const [sessionInfo, setSessionInfo] = useState(null);
    const [showQRModal, setShowQRModal] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [testNumber, setTestNumber] = useState('');
    const [showTestModal, setShowTestModal] = useState(false);
    const [logs, setLogs] = useState([]);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const pollCancelRef = useRef(null);

    useEffect(() => {
      const fresh = getChannelSettings('WHATSAPP');
      setForm(fresh.config);
      setEnabled(fresh.enabled);
      // Check actual WhatsApp connection status (but don't auto-poll or auto-show QR modal)
      checkWhatsAppStatus(false);
      
      // Cleanup: stop polling when component unmounts
      return () => {
        if (pollCancelRef.current) {
          pollCancelRef.current();
        }
      };
    }, [settings]); // eslint-disable-line

    const checkWhatsAppStatus = async (autoShowQR = true) => {
      try {
        const res = await axios.get('/api/whatsapp/status');
        const status = res.data.status || 'disconnected';
        setConnectionStatus(status);
        setSessionInfo(res.data.session || null);
        if (res.data.qr) {
          setQrCode(res.data.qr);
          // Only auto-show QR modal if explicitly requested (user action)
          if (autoShowQR && status === 'waiting_for_scan') {
            setShowQRModal(true);
            pollForQRCode();
          }
        }
        // If backend says connected but DB says disabled, auto-enable
        if (status === 'connected' && !enabled) {
          setEnabled(true);
          saveChannel('WHATSAPP', true, form);
        }
      } catch (error) {
        console.log('WhatsApp service not available');
        setConnectionStatus('service_unavailable');
      }
    };

    const initializeWhatsApp = async () => {
      setIsConnecting(true);
      setConnectionStatus('initializing');
      try {
        const res = await axios.post('/api/whatsapp/initialize', {
          sessionId: form.sessionId || 'default'
        });
        
        if (res.data.success) {
          setConnectionStatus(res.data.status || 'initializing');
          setShowQRModal(true);
          pollForQRCode();
        } else {
          toast.error(res.data.error || 'Failed to initialize WhatsApp');
          setConnectionStatus('disconnected');
        }
      } catch (error) {
        const errMsg = error.response?.data?.error || error.message || 'Failed to initialize WhatsApp service';
        toast.error(errMsg);
        setConnectionStatus('disconnected');
      } finally {
        setIsConnecting(false);
      }
    };

    const pollForQRCode = () => {
      const maxAttempts = 120;
      let attempts = 0;
      let cancelled = false;
      let lastQrTimestamp = null;

      // Store cancel function so we can stop polling on unmount/cancel
      pollCancelRef.current = () => { cancelled = true; };
      
      const poll = async () => {
        if (cancelled) return;
        
        try {
          // Use /status endpoint — it reliably returns 'connected' once ready
          const res = await axios.get('/api/whatsapp/status');
          
          if (cancelled) return;

          if (res.data.status === 'connected') {
            setConnectionStatus('connected');
            setSessionInfo(res.data.session || null);
            setQrCode('');
            setShowQRModal(false);
            toast.success('WhatsApp connected successfully!');
            pollCancelRef.current = null;
            return;
          }
          
          if (res.data.qr) {
            setQrCode(res.data.qr);
            setConnectionStatus('waiting_for_scan');
          } else if (res.data.status === 'authenticated') {
            // Scanned successfully, waiting for ready event
            setQrCode('');
            setConnectionStatus('authenticated');
          } else if (res.data.status === 'initializing') {
            setConnectionStatus('initializing');
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            // QR code timeout - automatically reinitialize to get a new QR code
            console.log('QR code timeout - generating new QR code...');
            setConnectionStatus('initializing');
            setQrCode('');
            attempts = 0; // Reset attempts
            // Reinitialize to get a new QR code
            await initializeWhatsApp();
          }
        } catch (error) {
          if (cancelled) return;
          console.log('Error polling status:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 3000);
          } else {
            // On max attempts, try to reinitialize
            console.log('Max polling attempts reached - reinitializing...');
            setConnectionStatus('initializing');
            attempts = 0;
            await initializeWhatsApp();
          }
        }
      };
      
      poll();
    };

    const disconnectWhatsApp = async () => {
      if (!window.confirm('Disconnect WhatsApp session? You will need to scan QR code again.')) return;
      
      try {
        await axios.post('/api/whatsapp/disconnect');
        setConnectionStatus('disconnected');
        setSessionInfo(null);
        setQrCode('');
        toast.success('WhatsApp disconnected');
      } catch (error) {
        toast.error('Failed to disconnect WhatsApp');
      }
    };

    const sendTestMessage = async () => {
      if (!testNumber) {
        toast.error('Please enter a test phone number');
        return;
      }
      
      try {
        setTesting(p => ({ ...p, whatsapp_test: true }));
        const res = await axios.post('/api/whatsapp/send-test', {
          to: testNumber,
          message: `🧪 Test Message from AGMS\n\nThis is a test message sent at ${new Date().toLocaleString()}\n\nIf you received this message, your WhatsApp integration is working correctly! ✅`
        });
        
        if (res.data.success) {
          toast.success('Test message sent successfully!');
          fetchWhatsAppLogs();
        } else {
          toast.error(res.data.error || 'Failed to send test message');
        }
      } catch (error) {
        toast.error('Failed to send test message');
      } finally {
        setTesting(p => ({ ...p, whatsapp_test: false }));
        setShowTestModal(false);
        setTestNumber('');
      }
    };

    const fetchWhatsAppLogs = async () => {
      try {
        const res = await axios.get('/api/whatsapp/logs?limit=50');
        setLogs(res.data.logs || []);
      } catch (error) {
        toast.error('Failed to load WhatsApp logs');
      }
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 'connected': return 'text-green-600 bg-green-100';
        case 'disconnected': return 'text-red-600 bg-red-100';
        case 'initializing': return 'text-blue-600 bg-blue-100';
        case 'waiting_for_scan': return 'text-yellow-600 bg-yellow-100';
        case 'service_unavailable': return 'text-gray-600 bg-gray-100';
        case 'authenticated': return 'text-blue-600 bg-blue-100';
        default: return 'text-gray-600 bg-gray-100';
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case 'connected': return 'Connected';
        case 'disconnected': return 'Disconnected';
        case 'initializing': return 'Initializing...';
        case 'waiting_for_scan': return 'Waiting for QR Scan';
        case 'service_unavailable': return 'Service Unavailable';
        case 'authenticated': return 'Authenticating...';
        default: return 'Unknown';
      }
    };

    const QRCodeModal = () => {
      return showQRModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-4">Connect WhatsApp</h3>
              
              {qrCode ? (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg border-2 border-green-200">
                    <img src={qrCode} alt="WhatsApp QR Code" className="w-full max-w-64 mx-auto" />
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg text-sm text-green-800">
                    <h4 className="font-medium mb-2">📱 How to connect:</h4>
                    <ol className="text-left space-y-1">
                      <li>1. Open WhatsApp on your phone</li>
                      <li>2. Go to Settings → Linked Devices</li>
                      <li>3. Tap "Link a Device"</li>
                      <li>4. Scan this QR code</li>
                    </ol>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Waiting for scan...
                  </div>
                </div>
              ) : connectionStatus === 'authenticated' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <CheckCircle className="w-16 h-16 text-green-500" />
                  </div>
                  <p className="text-green-700 font-medium">QR Scanned! Connecting...</p>
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Setting up session...
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                  <p className="text-gray-600">Generating QR code...</p>
                </div>
              )}
            </div>

            <div className="flex justify-center mt-6">
              <button onClick={() => { 
                setShowQRModal(false); 
                if (pollCancelRef.current) pollCancelRef.current();
                setConnectionStatus('disconnected'); 
              }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null;
    };

    const TestMessageModal = () => {
      return showTestModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Send Test WhatsApp Message</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Phone Number (with country code)</label>
                <input type="tel" value={testNumber} onChange={e => setTestNumber(e.target.value)}
                  placeholder="+1234567890" autoFocus
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
              </div>
              
              <div className="bg-green-50 p-3 rounded text-sm text-green-800">
                This will send a test message to verify your WhatsApp integration is working correctly.
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={sendTestMessage} disabled={testing.whatsapp_test}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 disabled:opacity-50">
                {testing.whatsapp_test ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Test Message
              </button>
              <button onClick={() => { setShowTestModal(false); setTestNumber(''); }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null;
    };

    const WhatsAppLogsModal = () => {
      const [expandedIndex, setExpandedIndex] = React.useState(null);

      const formatDate = (dateString) => new Date(dateString).toLocaleString();

      const getLogStatusColor = (status) => {
        switch (status) {
          case 'sent': return 'text-green-600 bg-green-100';
          case 'failed': return 'text-red-600 bg-red-100';
          case 'pending': return 'text-yellow-600 bg-yellow-100';
          default: return 'text-gray-600 bg-gray-100';
        }
      };

      // Strip any remaining @c.us / @lid suffix for display
      const formatRecipient = (to) => to ? to.replace(/@.*$/, '') : '-';

      return showLogsModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">WhatsApp Logs (Last 50)</h3>
              <button onClick={() => fetchWhatsAppLogs()}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {logs.length > 0 ? logs.map((log, index) => (
                <div key={index} className="border rounded p-3 text-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getLogStatusColor(log.status)}`}>
                        {log.status?.toUpperCase()}
                      </span>
                      <span className="text-gray-600">WhatsApp</span>
                    </div>
                    <span className="text-gray-500 text-xs">{formatDate(log.timestamp)}</span>
                  </div>

                  <div className="text-xs space-y-1">
                    <div><strong>To:</strong> {formatRecipient(log.to)}</div>
                    <div>
                      <strong>Message:</strong>{' '}
                      {log.message && log.message.length > 80 ? (
                        <>
                          {expandedIndex === index ? log.message : log.message.substring(0, 80) + '...'}
                          <button onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                            className="ml-1 text-blue-500 hover:underline">
                            {expandedIndex === index ? 'less' : 'more'}
                          </button>
                        </>
                      ) : (log.message || '-')}
                    </div>
                  </div>

                  {log.error && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-red-700 text-xs">
                      <strong>Error:</strong> {log.error}
                    </div>
                  )}
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500">
                  No WhatsApp logs found. Send a test message to see logs here.
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button onClick={() => setShowLogsModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null;
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold">WhatsApp Configuration</h3>
          </div>
          <button
            onClick={() => {
              const newEnabled = !enabled;
              setEnabled(newEnabled);
              saveChannel('WHATSAPP', newEnabled, form);
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-green-600' : 'bg-gray-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Connection Status */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Connection Status</h4>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(connectionStatus)}`}>
              {getStatusText(connectionStatus)}
            </span>
          </div>
          
          {sessionInfo && connectionStatus === 'connected' && (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="font-medium text-green-800">WhatsApp Connected</span>
              </div>
              <div className="text-green-700 space-y-1">
                <div><strong>Phone:</strong> {sessionInfo.phone || 'N/A'}</div>
                <div><strong>Name:</strong> {sessionInfo.name || 'N/A'}</div>
                <div><strong>Session:</strong> {form.sessionId || 'default'}</div>
                <div><strong>Connected:</strong> {sessionInfo.connectedAt ? new Date(sessionInfo.connectedAt).toLocaleString() : 'N/A'}</div>
              </div>
            </div>
          )}
          
          {connectionStatus === 'service_unavailable' && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="font-medium">WhatsApp Service Unavailable</span>
              </div>
              <p>The WhatsApp service is not running. Please ensure the WhatsApp service is started.</p>
            </div>
          )}
        </div>

        {/* Configuration */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Session ID</label>
            <input type="text" value={form.sessionId || ''} onChange={e => setForm({ ...form, sessionId: e.target.value })}
              placeholder="default" className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
            <p className="text-xs text-gray-500 mt-1">Unique identifier for this WhatsApp session</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL (optional)</label>
            <input type="url" value={form.webhookUrl || ''} onChange={e => setForm({ ...form, webhookUrl: e.target.value })}
              placeholder="https://your-domain.com/webhook/whatsapp" className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
            <p className="text-xs text-gray-500 mt-1">URL to receive WhatsApp webhook events</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button onClick={() => saveChannel('WHATSAPP', enabled, form)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Save WhatsApp Settings
          </button>
          
          {(connectionStatus === 'disconnected' || connectionStatus === 'initializing') && enabled && (
            <button onClick={initializeWhatsApp} disabled={isConnecting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50">
              {isConnecting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
              {connectionStatus === 'initializing' ? 'Retry Connection' : 'Connect WhatsApp'}
            </button>
          )}
          
          {connectionStatus === 'waiting_for_scan' && (
            <button onClick={() => setShowQRModal(true)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 flex items-center gap-2 animate-pulse">
              <Eye className="w-4 h-4" /> Show QR Code
            </button>
          )}
          
          {connectionStatus === 'connected' && (
            <>
              <button onClick={disconnectWhatsApp}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Disconnect
              </button>
              
              <button onClick={() => setShowTestModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2">
                <Send className="w-4 h-4" /> Send Test Message
              </button>
            </>
          )}
          
          <button onClick={() => { fetchWhatsAppLogs(); setShowLogsModal(true); }}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2">
            <Eye className="w-4 h-4" /> View Logs
          </button>
          
          <button onClick={() => checkWhatsAppStatus(true)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh Status
          </button>
        </div>

        {/* Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">📱 WhatsApp Integration Info</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Uses WhatsApp Web API for reliable message delivery</li>
            <li>• Requires QR code scan to establish connection</li>
            <li>• Session persists until manually disconnected</li>
            <li>• Supports text messages with emojis and formatting</li>
            <li>• Messages are sent from your personal/business WhatsApp account</li>
          </ul>
        </div>

        <QRCodeModal />
        <TestMessageModal />
        <WhatsAppLogsModal />
      </div>
    );
  };

  const TemplatePanel = () => {
    const [selectedChannel, setSelectedChannel] = useState('EMAIL');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEventType, setSelectedEventType] = useState('');
    const [previewTemplate, setPreviewTemplate] = useState(null);
    
    const channelTemplates = templates.filter(t => {
      const matchesChannel = t.channel === selectedChannel;
      const matchesSearch = !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           t.eventType.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesEvent = !selectedEventType || t.eventType === selectedEventType;
      return matchesChannel && matchesSearch && matchesEvent;
    });

    const eventTypes = [...new Set(templates.filter(t => t.channel === selectedChannel).map(t => t.eventType))];
    const enabledCount = channelTemplates.filter(t => t.enabled).length;

    const getEventTypeColor = (eventType) => {
      const colors = {
        'USER_APPROVED': 'bg-green-100 text-green-700',
        'INVOICE_CREATED': 'bg-blue-100 text-blue-700',
        'JOB_CARD_COMPLETED': 'bg-purple-100 text-purple-700',
        'PAYMENT_RECEIVED': 'bg-emerald-100 text-emerald-700',
        'DAILY_BACKUP_REMINDER': 'bg-orange-100 text-orange-700'
      };
      return colors[eventType] || 'bg-gray-100 text-gray-600';
    };

    const PreviewModal = () => {
      if (!previewTemplate) return null;
      
      const sampleData = {
        name: 'John Doe',
        username: 'johndoe',
        date: new Date().toLocaleDateString(),
        customer_name: 'ABC Company Ltd',
        invoice_number: 'INV-2024-001',
        amount: '$1,250.00',
        due_date: new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString(),
        job_card_number: 'JC-2024-001',
        vehicle_info: '2020 Toyota Camry - ABC123',
        payment_method: 'Credit Card',
        reference: 'REF123456'
      };

      const renderPreview = (text) => {
        let preview = text;
        Object.entries(sampleData).forEach(([key, value]) => {
          preview = preview.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), value);
        });
        return preview;
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Template Preview</h3>
              <button onClick={() => setPreviewTemplate(null)}
                className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  {selectedChannel === 'EMAIL' ? <Mail className="w-4 h-4 text-blue-600" /> : <MessageSquare className="w-4 h-4 text-green-600" />}
                  <span className="font-medium">{previewTemplate.name}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${getEventTypeColor(previewTemplate.eventType)}`}>
                  {previewTemplate.eventType}
                </span>
              </div>

              {previewTemplate.subject && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <div className="p-3 bg-blue-50 rounded border">
                    {renderPreview(previewTemplate.subject)}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <div className="p-4 bg-gray-50 rounded border min-h-32 whitespace-pre-wrap">
                  {renderPreview(previewTemplate.body)}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                <strong>Note:</strong> This preview uses sample data. Actual notifications will use real data from your system.
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setEditingTemplate(previewTemplate); setShowTemplateModal(true); setPreviewTemplate(null); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2">
                <Edit3 className="w-4 h-4" /> Edit Template
              </button>
              <button onClick={() => setPreviewTemplate(null)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold">Notification Templates</h3>
            <span className="text-sm text-gray-500">({channelTemplates.length} total, {enabledCount} active)</span>
          </div>
          <button onClick={() => { setEditingTemplate({ channel: selectedChannel, enabled: true }); setShowTemplateModal(true); }}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>

        {/* Channel Tabs */}
        <div className="flex gap-2">
          <button onClick={() => { setSelectedChannel('EMAIL'); setSearchTerm(''); setSelectedEventType(''); }}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors ${
              selectedChannel === 'EMAIL' 
                ? 'bg-blue-100 text-blue-700 border-2 border-blue-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            <Mail className="w-4 h-4" /> 
            Email Templates
            <span className="bg-white px-2 py-1 rounded text-xs">
              {templates.filter(t => t.channel === 'EMAIL').length}
            </span>
          </button>
          <button onClick={() => { setSelectedChannel('WHATSAPP'); setSearchTerm(''); setSelectedEventType(''); }}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors ${
              selectedChannel === 'WHATSAPP' 
                ? 'bg-green-100 text-green-700 border-2 border-green-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            <MessageSquare className="w-4 h-4" /> 
            WhatsApp Templates
            <span className="bg-white px-2 py-1 rounded text-xs">
              {templates.filter(t => t.channel === 'WHATSAPP').length}
            </span>
          </button>
          <button onClick={() => setShowDefaultTemplatesModal(true)}
            className="px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200">
            <RefreshCw className="w-4 h-4" /> 
            Default Templates
            <span className="bg-white px-2 py-1 rounded text-xs">
              {defaultTemplates.length}
            </span>
          </button>
        </div>

        {/* Debug info - remove in production */}
        {defaultTemplates.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-900 mb-2">Default Templates Status</h4>
            <p className="text-sm text-yellow-800 mb-2">
              No default templates found. They should be created automatically.
            </p>
            <button onClick={createDefaultTemplates}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm">
              Create Default Templates Now
            </button>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex gap-4 items-center bg-gray-50 p-4 rounded-lg">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search templates by name or event type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>
          <div className="w-48">
            <select
              value={selectedEventType}
              onChange={(e) => setSelectedEventType(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none"
            >
              <option value="">All Event Types</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid gap-4">
          {channelTemplates.map(template => (
            <div key={template.id} className={`border-2 rounded-lg p-5 bg-white transition-all hover:shadow-md ${
              template.enabled ? 'border-green-200 bg-green-50/30' : 'border-gray-200'
            }`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {selectedChannel === 'EMAIL' ? 
                      <Mail className="w-5 h-5 text-blue-600" /> : 
                      <MessageSquare className="w-5 h-5 text-green-600" />
                    }
                    <h4 className="font-semibold text-lg">{template.name}</h4>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${getEventTypeColor(template.eventType)}`}>
                    {template.eventType.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleTemplate(template)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      template.enabled 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}>
                    {template.enabled ? '✓ Enabled' : '✗ Disabled'}
                  </button>
                </div>
              </div>

              {template.subject && (
                <div className="mb-3">
                  <span className="text-sm font-medium text-gray-600">Subject: </span>
                  <span className="text-sm text-gray-800">{template.subject}</span>
                </div>
              )}

              <div className="mb-4">
                <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">
                  {template.body.length > 200 ? `${template.body.substring(0, 200)}...` : template.body}
                </p>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Created: {new Date(template.createdAt || Date.now()).toLocaleDateString()}</span>
                  {template.updatedAt && (
                    <span>Updated: {new Date(template.updatedAt).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPreviewTemplate(template)}
                    className="px-3 py-1 text-purple-600 hover:bg-purple-50 rounded-md text-sm font-medium flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Preview
                  </button>
                  <button onClick={() => { setEditingTemplate(template); setShowTemplateModal(true); }}
                    className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md text-sm font-medium flex items-center gap-1">
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => deleteTemplate(template.id)}
                    className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {channelTemplates.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No {selectedChannel.toLowerCase()} templates found
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || selectedEventType 
                  ? 'Try adjusting your search or filter criteria.' 
                  : `Create your first ${selectedChannel.toLowerCase()} template to get started.`
                }
              </p>
              {!searchTerm && !selectedEventType && (
                <button onClick={() => { setEditingTemplate({ channel: selectedChannel, enabled: true }); setShowTemplateModal(true); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2 mx-auto">
                  <Plus className="w-4 h-4" /> Create Template
                </button>
              )}
            </div>
          )}
        </div>

        <PreviewModal />
      </div>
    );
  };

  const DefaultTemplatesModal = () => {
    const [selectedChannel, setSelectedChannel] = useState('EMAIL');
    const [editingDefault, setEditingDefault] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    
    const channelDefaults = defaultTemplates.filter(t => t.channel === selectedChannel);
    
    const getEventTypeColor = (eventType) => {
      const colors = {
        'USER_APPROVED': 'bg-green-100 text-green-700',
        'INVOICE_CREATED': 'bg-blue-100 text-blue-700',
        'JOB_CARD_COMPLETED': 'bg-purple-100 text-purple-700',
        'PAYMENT_RECEIVED': 'bg-emerald-100 text-emerald-700',
        'DAILY_BACKUP_REMINDER': 'bg-orange-100 text-orange-700'
      };
      return colors[eventType] || 'bg-gray-100 text-gray-600';
    };

    const EditDefaultModal = () => {
      const [form, setForm] = useState(editingDefault || {});
      
      useEffect(() => {
        if (editingDefault) setForm(editingDefault);
      }, [editingDefault]);

      const handleSave = () => {
        updateDefaultTemplate(form);
        setShowEditModal(false);
        setEditingDefault(null);
      };

      return showEditModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-lg font-semibold">Edit Default Template</h3>
                <p className="text-sm text-gray-500">{form.name}</p>
              </div>
              <button onClick={() => { setShowEditModal(false); setEditingDefault(null); }}
                className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {form.channel === 'EMAIL' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Subject</label>
                  <input type="text" value={form.subject || ''} onChange={e => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message Content</label>
                <textarea value={form.body || ''} onChange={e => setForm({ ...form, body: e.target.value })}
                  rows={12} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none font-mono text-sm" />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.enabled || false} onChange={e => setForm({ ...form, enabled: e.target.checked })}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                <span className="text-sm font-medium">Enable this template</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-6 border-t bg-gray-50">
              <button onClick={() => resetDefaultTemplate(form.id)}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> Reset to Default
              </button>
              <div className="flex gap-3">
                <button onClick={() => { setShowEditModal(false); setEditingDefault(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleSave}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2">
                  <Save className="w-4 h-4" /> Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null;
    };

    return showDefaultTemplatesModal ? (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h3 className="text-xl font-semibold">Default Templates Management</h3>
              <p className="text-sm text-gray-500 mt-1">Edit and manage system default notification templates</p>
            </div>
            <button onClick={() => setShowDefaultTemplatesModal(false)}
              className="text-gray-400 hover:text-gray-600">
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="flex border-b">
            <button onClick={() => setSelectedChannel('EMAIL')}
              className={`px-6 py-3 font-medium ${selectedChannel === 'EMAIL' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Mail className="w-4 h-4 inline mr-2" />Email Templates ({defaultTemplates.filter(t => t.channel === 'EMAIL').length})
            </button>
            <button onClick={() => setSelectedChannel('WHATSAPP')}
              className={`px-6 py-3 font-medium ${selectedChannel === 'WHATSAPP' ? 'border-b-2 border-green-500 text-green-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <MessageSquare className="w-4 h-4 inline mr-2" />WhatsApp Templates ({defaultTemplates.filter(t => t.channel === 'WHATSAPP').length})
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-900 mb-2">About Default Templates</h4>
              <p className="text-sm text-blue-700">
                These are system-wide default templates that are automatically used for notifications. 
                You can edit them to match your organization's communication style, or reset them to original defaults at any time.
              </p>
            </div>

            <div className="grid gap-4">
              {channelDefaults.map(template => (
                <div key={template.id} className={`border-2 rounded-lg p-5 transition-all ${
                  template.enabled ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {selectedChannel === 'EMAIL' ? 
                          <Mail className="w-5 h-5 text-blue-600" /> : 
                          <MessageSquare className="w-5 h-5 text-green-600" />
                        }
                        <h4 className="font-semibold text-lg">{template.name}</h4>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${getEventTypeColor(template.eventType)}`}>
                        {template.eventType.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 font-medium">
                        DEFAULT
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleDefaultTemplate(template)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          template.enabled 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}>
                        {template.enabled ? '✓ Enabled' : '✗ Disabled'}
                      </button>
                    </div>
                  </div>

                  {template.subject && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-600">Subject: </span>
                      <span className="text-sm text-gray-800">{template.subject}</span>
                    </div>
                  )}

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">
                      {template.body.length > 200 ? `${template.body.substring(0, 200)}...` : template.body}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-500">
                      System default template for {template.eventType.replace(/_/g, ' ').toLowerCase()}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingDefault(template); setShowEditModal(true); }}
                        className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md text-sm font-medium flex items-center gap-1">
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                      <button onClick={() => resetDefaultTemplate(template.id)}
                        className="px-3 py-1 text-orange-600 hover:bg-orange-50 rounded-md text-sm font-medium flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" /> Reset
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {channelDefaults.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <RefreshCw className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">
                    No default {selectedChannel.toLowerCase()} templates found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Default templates will be created automatically when the system starts.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50">
            <button onClick={() => setShowDefaultTemplatesModal(false)}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
        <EditDefaultModal />
      </div>
    ) : null;
  };

  const TestEmailModal = () => {
    return showTestModal ? (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold mb-4">Send Test Email</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Test Email Address</label>
              <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                placeholder="test@example.com" autoFocus
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
            </div>
            
            <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
              This will send a test email to verify your SMTP configuration is working correctly.
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={sendTestEmail} disabled={testing.testEmail}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 disabled:opacity-50">
              {testing.testEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Test Email
            </button>
            <button onClick={() => { setShowTestModal(false); setTestEmail(''); }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    ) : null;
  };

  const LogsModal = () => {
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 'SUCCESS': return 'text-green-600 bg-green-100';
        case 'FAILED': return 'text-red-600 bg-red-100';
        case 'PENDING': return 'text-yellow-600 bg-yellow-100';
        default: return 'text-gray-600 bg-gray-100';
      }
    };

    return showLogsModal ? (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Email Logs (Last 50)</h3>
            <button onClick={() => fetchLogs()}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logs.length > 0 ? logs.map(log => (
              <div key={log.id} className="border rounded p-3 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.status)}`}>
                      {log.status}
                    </span>
                    <span className="text-gray-600">{log.channel}</span>
                    <span className="text-gray-600">{log.eventType}</span>
                  </div>
                  <span className="text-gray-500 text-xs">{formatDate(log.createdAt)}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <strong>To:</strong> {log.recipient}
                  </div>
                  <div>
                    <strong>Subject:</strong> {log.subject}
                  </div>
                </div>
                
                {log.errorMessage && (
                  <div className="mt-2 p-2 bg-red-50 rounded text-red-700 text-xs">
                    <strong>Error:</strong> {log.errorMessage}
                  </div>
                )}
                
                {log.sentAt && (
                  <div className="mt-1 text-xs text-green-600">
                    <strong>Sent:</strong> {formatDate(log.sentAt)}
                  </div>
                )}
              </div>
            )) : (
              <div className="text-center py-8 text-gray-500">
                No email logs found. Send a test email to see logs here.
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button onClick={() => setShowLogsModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    ) : null;
  };

  const TemplateModal = () => {
    const [form, setForm] = useState(editingTemplate || { name: '', eventType: '', channel: 'EMAIL', subject: '', body: '', enabled: true });
    const [activeTab, setActiveTab] = useState('basic');
    const [showVariables, setShowVariables] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);

    useEffect(() => {
      if (editingTemplate) {
        setForm(editingTemplate);
      } else {
        setForm({ name: '', eventType: '', channel: 'EMAIL', subject: '', body: '', enabled: true });
      }
    }, [editingTemplate]);

    const eventTypes = [
      { value: 'USER_APPROVED', label: 'User Approved', description: 'When a user account is approved' },
      { value: 'INVOICE_CREATED', label: 'Invoice Created', description: 'When a new invoice is generated' },
      { value: 'JOB_CARD_COMPLETED', label: 'Job Card Completed', description: 'When a job card is marked as completed' },
      { value: 'PAYMENT_RECEIVED', label: 'Payment Received', description: 'When a payment is successfully processed' },
      { value: 'DAILY_BACKUP_REMINDER', label: 'Daily Backup Reminder', description: 'Daily system backup notifications' }
    ];

    const templateSuggestions = {
      EMAIL: {
        USER_APPROVED: {
          subject: 'Welcome to AGMS - Your Account is Approved!',
          body: `Dear {{name}},\n\nGreat news! Your AGMS account has been approved and is now active.\n\nAccount Details:\n- Username: {{username}}\n- Approval Date: {{date}}\n\nYou can now log in to the system and start using all available features.\n\nIf you have any questions, please don't hesitate to contact our support team.\n\nBest regards,\nAGMS Team`
        },
        INVOICE_CREATED: {
          subject: 'New Invoice #{{invoice_number}} - {{customer_name}}',
          body: `Dear {{customer_name}},\n\nA new invoice has been created for your account.\n\nInvoice Details:\n- Invoice Number: {{invoice_number}}\n- Amount: {{amount}}\n- Due Date: {{due_date}}\n- Created: {{date}}\n\nPlease review the invoice and process payment by the due date.\n\nThank you for your business!\n\nBest regards,\nAGMS Team`
        },
        JOB_CARD_COMPLETED: {
          subject: 'Job Card #{{job_card_number}} Completed',
          body: `Dear {{customer_name}},\n\nYour job card has been completed successfully.\n\nJob Details:\n- Job Card Number: {{job_card_number}}\n- Vehicle: {{vehicle_info}}\n- Completion Date: {{date}}\n\nYour vehicle is ready for pickup. Please contact us to schedule a convenient time.\n\nThank you for choosing our services!\n\nBest regards,\nAGMS Team`
        },
        PAYMENT_RECEIVED: {
          subject: 'Payment Confirmation - {{reference}}',
          body: `Dear {{customer_name}},\n\nWe have successfully received your payment.\n\nPayment Details:\n- Amount: {{amount}}\n- Payment Method: {{payment_method}}\n- Reference: {{reference}}\n- Date: {{date}}\n\nThank you for your prompt payment!\n\nBest regards,\nAGMS Team`
        }
      },
      WHATSAPP: {
        USER_APPROVED: {
          body: `🎉 Welcome to AGMS!\n\nHi {{name}}, your account has been approved!\n\n✅ Username: {{username}}\n📅 Approved: {{date}}\n\nYou can now access the system. Welcome aboard! 🚀`
        },
        INVOICE_CREATED: {
          body: `📄 New Invoice Created\n\nHi {{customer_name}},\n\n🧾 Invoice: {{invoice_number}}\n💰 Amount: {{amount}}\n📅 Due: {{due_date}}\n\nPlease review and pay by due date. Thank you! 🙏`
        },
        JOB_CARD_COMPLETED: {
          body: `✅ Job Completed!\n\nHi {{customer_name}},\n\n🔧 Job Card: {{job_card_number}}\n🚗 Vehicle: {{vehicle_info}}\n📅 Completed: {{date}}\n\nYour vehicle is ready for pickup! 🎉`
        },
        PAYMENT_RECEIVED: {
          body: `💳 Payment Received\n\nHi {{customer_name}},\n\n✅ Amount: {{amount}}\n🏦 Method: {{payment_method}}\n📋 Ref: {{reference}}\n\nThank you for your payment! 🙏`
        }
      }
    };

    const availableVariables = [
      { name: 'name', description: 'User\'s full name' },
      { name: 'username', description: 'User\'s username' },
      { name: 'date', description: 'Current date' },
      { name: 'customer_name', description: 'Customer\'s name' },
      { name: 'invoice_number', description: 'Invoice number' },
      { name: 'amount', description: 'Amount value' },
      { name: 'due_date', description: 'Due date' },
      { name: 'job_card_number', description: 'Job card number' },
      { name: 'vehicle_info', description: 'Vehicle information' },
      { name: 'payment_method', description: 'Payment method' },
      { name: 'reference', description: 'Reference number' }
    ];

    const insertVariable = (variable) => {
      const textarea = document.querySelector('#template-body');
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = form.body || '';
        const before = text.substring(0, start);
        const after = text.substring(end);
        const newText = before + `{{${variable}}}` + after;
        setForm({ ...form, body: newText });
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
        }, 0);
      }
    };

    const applySuggestion = () => {
      const suggestion = templateSuggestions[form.channel]?.[form.eventType];
      if (suggestion) {
        setForm({
          ...form,
          subject: form.channel === 'EMAIL' ? suggestion.subject : form.subject,
          body: suggestion.body
        });
      }
    };

    const renderPreview = () => {
      const sampleData = {
        name: 'John Doe',
        username: 'johndoe',
        date: new Date().toLocaleDateString(),
        customer_name: 'ABC Company Ltd',
        invoice_number: 'INV-2024-001',
        amount: '$1,250.00',
        due_date: new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString(),
        job_card_number: 'JC-2024-001',
        vehicle_info: '2020 Toyota Camry - ABC123',
        payment_method: 'Credit Card',
        reference: 'REF123456'
      };

      const renderText = (text) => {
        let preview = text || '';
        Object.entries(sampleData).forEach(([key, value]) => {
          preview = preview.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), value);
        });
        return preview;
      };

      return (
        <div className="space-y-4">
          {form.channel === 'EMAIL' && form.subject && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject Preview</label>
              <div className="p-3 bg-blue-50 rounded border">
                {renderText(form.subject)}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message Preview</label>
            <div className="p-4 bg-gray-50 rounded border min-h-32 whitespace-pre-wrap">
              {renderText(form.body) || 'Enter message content to see preview...'}
            </div>
          </div>
        </div>
      );
    };

    const validateForm = () => {
      const errors = [];
      if (!form.name?.trim()) errors.push('Template name is required');
      if (!form.eventType) errors.push('Event type is required');
      if (!form.body?.trim()) errors.push('Message body is required');
      if (form.channel === 'EMAIL' && !form.subject?.trim()) errors.push('Email subject is required');
      return errors;
    };

    const handleSave = () => {
      const errors = validateForm();
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      saveTemplate(form);
    };

    return showTemplateModal ? (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h3 className="text-xl font-semibold">{form.id ? 'Edit Template' : 'Create New Template'}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {form.channel === 'EMAIL' ? 'Email notification template' : 'WhatsApp message template'}
              </p>
            </div>
            <button onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
              className="text-gray-400 hover:text-gray-600">
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b">
            <button onClick={() => setActiveTab('basic')}
              className={`px-6 py-3 font-medium ${activeTab === 'basic' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}>
              Basic Info
            </button>
            <button onClick={() => setActiveTab('content')}
              className={`px-6 py-3 font-medium ${activeTab === 'content' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}>
              Content
            </button>
            <button onClick={() => setActiveTab('preview')}
              className={`px-6 py-3 font-medium ${activeTab === 'preview' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}>
              Preview
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                    <input type="text" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g., Welcome Email, Invoice Notification"
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Channel</label>
                    <select value={form.channel || 'EMAIL'} onChange={e => setForm({ ...form, channel: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none">
                      <option value="EMAIL">📧 Email</option>
                      <option value="WHATSAPP">💬 WhatsApp</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Type *</label>
                  <div className="grid grid-cols-1 gap-3">
                    {eventTypes.map(type => (
                      <label key={type.value} className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        form.eventType === type.value ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input type="radio" name="eventType" value={type.value}
                          checked={form.eventType === type.value}
                          onChange={e => setForm({ ...form, eventType: e.target.value })}
                          className="sr-only" />
                        <div className="flex-1">
                          <div className="font-medium">{type.label}</div>
                          <div className="text-sm text-gray-500">{type.description}</div>
                        </div>
                        {form.eventType === type.value && <CheckCircle className="w-5 h-5 text-purple-600" />}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.enabled || false} onChange={e => setForm({ ...form, enabled: e.target.checked })}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                      <span className="font-medium">Enable this template</span>
                    </label>
                    <p className="text-sm text-gray-500 mt-1">Template will be active and used for notifications</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div className="space-y-6">
                {/* Suggestion Banner */}
                {form.eventType && templateSuggestions[form.channel]?.[form.eventType] && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-blue-900">Template Suggestion Available</h4>
                        <p className="text-sm text-blue-700 mt-1">We have a pre-written template for this event type.</p>
                      </div>
                      <button onClick={applySuggestion}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
                        Use Suggestion
                      </button>
                    </div>
                  </div>
                )}

                {form.channel === 'EMAIL' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Subject *</label>
                    <input type="text" value={form.subject || ''} onChange={e => setForm({ ...form, subject: e.target.value })}
                      placeholder="Use {{variable}} for dynamic content"
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Message Content *</label>
                    <button onClick={() => setShowVariables(!showVariables)}
                      className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {showVariables ? 'Hide' : 'Show'} Variables
                    </button>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <textarea id="template-body" value={form.body || ''} onChange={e => setForm({ ...form, body: e.target.value })}
                        placeholder={form.channel === 'EMAIL' 
                          ? "Write your email content here...\n\nUse {{variable}} for dynamic content like {{name}}, {{date}}, {{amount}}"
                          : "Write your WhatsApp message here...\n\nUse {{variable}} for dynamic content like {{name}}, {{date}}, {{amount}}"
                        }
                        rows={12} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none font-mono text-sm" />
                    </div>
                    
                    {showVariables && (
                      <div className="w-64 bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Available Variables</h4>
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {availableVariables.map(variable => (
                            <button key={variable.name} onClick={() => insertVariable(variable.name)}
                              className="w-full text-left p-2 rounded hover:bg-white border border-transparent hover:border-gray-200 transition-colors">
                              <div className="font-mono text-sm text-purple-600">{`{{${variable.name}}}`}</div>
                              <div className="text-xs text-gray-500">{variable.description}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">💡 Tips for Better Templates</h4>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• Use clear, concise language</li>
                    <li>• Include all necessary information</li>
                    <li>• Test with sample data before enabling</li>
                    {form.channel === 'WHATSAPP' && <li>• Keep messages under 1600 characters for best delivery</li>}
                    {form.channel === 'EMAIL' && <li>• Write compelling subject lines to improve open rates</li>}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Template Preview</h4>
                  <p className="text-sm text-blue-700">This shows how your template will look with sample data.</p>
                </div>
                {renderPreview()}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t bg-gray-50">
            <div className="text-sm text-gray-500">
              {validateForm().length > 0 && (
                <span className="text-red-600">Please fix {validateForm().length} error(s) before saving</span>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={validateForm().length > 0}
                className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                <Save className="w-4 h-4" /> {form.id ? 'Update' : 'Create'} Template
              </button>
            </div>
          </div>
        </div>
      </div>
    ) : null;
  };

  if (loading) {
    return (
      <div className="max-w-4xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Notifications</h2>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading notification settings...</span>
        </div>
      </div>
    );
  }

  const emailStatus = getChannelSettings('EMAIL');
  const whatsappStatus = getChannelSettings('WHATSAPP');

  return (
    <div className="max-w-6xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Notifications</h2>
      <p className="text-gray-500 mb-6">Configure and manage all system notifications.</p>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button onClick={() => setActiveTab('control-panel')}
          className={`px-6 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'control-panel' 
              ? 'border-blue-500 text-blue-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <Settings className="w-4 h-4 inline mr-2" />
          Control Panel
        </button>
        <button onClick={() => setActiveTab('channels')}
          className={`px-6 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'channels' 
              ? 'border-blue-500 text-blue-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <Mail className="w-4 h-4 inline mr-2" />
          Channel Settings
        </button>
        <button onClick={() => setActiveTab('templates')}
          className={`px-6 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'templates' 
              ? 'border-blue-500 text-blue-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <FileText className="w-4 h-4 inline mr-2" />
          Templates
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'control-panel' && <NotificationControlPanel onNavigate={setActiveTab} />}
      
      {activeTab === 'channels' && (
        <div>
          {/* Channel status summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
        <button onClick={() => setActiveChannel('email')}
          className={`p-4 rounded-lg border-2 text-left transition-colors ${activeChannel === 'email' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Email / SMTP</span>
            </div>
            {emailStatus.enabled
              ? <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle className="w-3 h-3" /> Active</span>
              : <span className="flex items-center gap-1 text-xs text-gray-400"><XCircle className="w-3 h-3" /> Disabled</span>}
          </div>
        </button>
        <button onClick={() => setActiveChannel('whatsapp')}
          className={`p-4 rounded-lg border-2 text-left transition-colors ${activeChannel === 'whatsapp' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              <span className="font-medium">WhatsApp</span>
            </div>
            {whatsappStatus.enabled
              ? <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle className="w-3 h-3" /> Active</span>
              : <span className="flex items-center gap-1 text-xs text-gray-400"><XCircle className="w-3 h-3" /> Disabled</span>}
          </div>
        </button>
      </div>

          {/* Active channel config */}
          <div className="bg-white rounded-lg shadow p-6">
            {activeChannel === 'email' && <EmailPanel />}
            {activeChannel === 'whatsapp' && <WhatsAppPanel />}
          </div>
        </div>
      )}
      
      {activeTab === 'templates' && (
        <div className="bg-white rounded-lg shadow p-6">
          <TemplatePanel />
        </div>
      )}
      
      <TemplateModal />
      <TestEmailModal />
      <LogsModal />
      <DefaultTemplatesModal />
    </div>
  );
};

export default NotificationSettings;
